import useScrollIntoView from '@/hooks/useScrollIntoView';
import DropdownButton, {
  DropdownButtonSize,
} from '@/storybook/components/FormCollection/components/DropdownButton/DropdownButton';
import DropdownList from '@/storybook/components/FormCollection/components/DropdownList/DropdownList';
import { useFloatingDropdownPanel } from '@/storybook/components/FormCollection/components/DropdownList/useFloatingDropdownPanel';
import DropdownRatingPanel from '@/storybook/components/FormCollection/components/DropdownRatingPanel/DropdownRatingPanel';
import DropdownValueWithRatingStars from '@/storybook/components/FormCollection/components/DropdownValueWithRatingStars/DropdownValueWithRatingStars';
import { DropdownPosition, handleDropdownListPosition } from '@/storybook/components/FormCollection/utils/utils';
import cx from 'classnames';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SingleLevelSelectFloatingLabel.module.css';

export type SingleLevelSelectFloatingLabelProps<T = number> = {
  'tooltip'?: ReactNode;
  'value'?: T;
  'isLoading'?: boolean;
  'onChange': (selectedValue: T | undefined) => void;
  'onClear'?: VoidFunction;
  'placeholder'?: string;
  'disabled'?: boolean;
  'className'?: string;
  'clearable'?: boolean;
  'comingSoon'?: boolean;
  'adobeEventTrackFunctions'?: () => void;
  'onDropdownToggle'?: (isOpen: boolean) => void;
  'alignment'?: 'vertical' | 'horizontal';
  'data-testid'?: string;
  'dropdownButtonTestId'?: string;
  'dropdownListTestId'?: string;
  'dropdownPosition'?: DropdownPosition;
  'size'?: keyof typeof DropdownButtonSize;
  'state'?: 'default' | 'filled' | 'error';
};

export default function SingleLevelSelectFloatingLabel<T extends number>({
  tooltip,
  value,
  isLoading,
  onChange,
  onClear,
  placeholder,
  disabled = false,
  className,
  clearable = true,
  comingSoon,
  adobeEventTrackFunctions,
  onDropdownToggle,
  alignment = 'vertical',
  'data-testid': dataTestId = 'singleLevelSelectFloatingLabel',
  dropdownButtonTestId,
  dropdownListTestId,
  dropdownPosition,
  size = 'L',
  state = 'default',
}: SingleLevelSelectFloatingLabelProps<T>) {
  const [dropdownListPosition, setDropdownListPosition] = useState<DropdownPosition>(dropdownPosition ?? 'down');
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const { dropdownRef, panelRef } = useFloatingDropdownPanel({ isOpen, onClose: () => setIsOpen(false) });
  const dropdownButtonRef = useRef<HTMLDivElement>(null);

  useScrollIntoView({
    elementRef: dropdownRef,
    isVisible: isOpen,
  });

  const handleToggleDropdown = useCallback(() => {
    if (disabled || comingSoon) return;
    setIsOpen(prev => {
      const newState = !prev;
      if (newState) {
        handleDropdownListPosition(dropdownPosition, dropdownRef, setDropdownListPosition);
      }
      return newState;
    });
  }, [disabled, comingSoon, dropdownPosition, dropdownRef]);

  useEffect(() => {
    onDropdownToggle?.(isOpen);
  }, [isOpen, onDropdownToggle]);

  const handleClickOption = useCallback(
    (optionValue: T) => {
      if (disabled) return;
      onChange(optionValue);
      setIsOpen(false);
    },
    [onChange, disabled],
  );

  const handleOnClearValue = useCallback(() => {
    if (disabled || !clearable) return;
    if (onClear) {
      onClear();
    } else {
      onChange(undefined);
    }
  }, [onClear, disabled, clearable, onChange]);

  const hasValue = value != null && value > 0;
  const isLabelFloating = hasValue || isOpen;
  const displayLabel = placeholder ? `${t('components.levelSelectDropdown.levelTopicPrefix')} ${placeholder}` : '';

  return (
    <div
      ref={dropdownRef}
      className={cx(styles.floatingLabelContainer, className)}
      data-alignment={alignment}
      data-testid={dataTestId}
      onClick={adobeEventTrackFunctions}
      data-state={state}
    >
      <section className={styles.dropdownContainer}>
        {displayLabel && (
          <label className={styles.floatingLabel} data-floating={isLabelFloating} data-has-value={hasValue}>
            {displayLabel}
          </label>
        )}
        <div className={styles.buttonWrapper}>
          <DropdownButton
            ref={dropdownButtonRef}
            type="singleLevelSelect"
            size={size}
            placeholder=""
            active={isOpen}
            onClick={!disabled && !comingSoon ? handleToggleDropdown : undefined}
            onClear={handleOnClearValue}
            clearable={clearable}
            disabled={comingSoon || disabled}
            data-testid={dropdownButtonTestId}
            numOfValue={value ?? 0}
          >
            <div className={styles.value}>
              {hasValue && (
                <DropdownValueWithRatingStars
                  value={[{ label: placeholder ?? '', value: placeholder ?? '', level: value ?? 0 }]}
                />
              )}
            </div>
          </DropdownButton>
        </div>
        <DropdownList
          isOpen={isOpen}
          isLoading={isLoading}
          disabled={disabled}
          position={dropdownListPosition}
          data-testid={dropdownListTestId}
          listRef={null}
          dropdownTriggerRef={dropdownButtonRef}
          isFloating
          panelRef={panelRef}
          numberOfSearchResults={1}
          optionsCount={6}
        >
          <DropdownRatingPanel onClick={handleClickOption} data-testid="singleLevelSelectFloatingLabelRatingPanel" />
        </DropdownList>
      </section>
      {tooltip && <div className={styles.tooltip}>{tooltip}</div>}
    </div>
  );
}
